import clsx from 'clsx';
import * as React from 'react';
import { Pressable } from 'react-aria';
import { atom, useRecoilState } from 'recoil';

import {
    expandable,
    FRAGMENT_EXPANDABLE_BODY,
    FRAGMENT_EXPANDABLE_TITLE,
    type SectionIDs,
} from '@gitbook/doc-core';
import { type Block, isBlockRangeSelection, TextRange } from '@gitbook/slate';
import { type BlockProps, FragmentRenderer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { BlockWrapper, useBlockAnchor, VoidBlockFrame } from '../common';
import { BlockVariantContext, EditorSectionIDsContext } from '../contexts';

interface ExpandablesState {
    [key: string]: 'expanded' | 'collapsed';
}
const defaultState: ExpandablesState = {};
export const expandablesAtom = atom<ExpandablesState>({
    key: 'expandablesAtom',
    default: defaultState,
});

/**
 * Renderer for an expandable block.
 */
export function BlockExpandable(props: BlockProps) {
    const { node, isFocused, editor } = props;
    const hasHashInBody = useHasHashInBody(node);
    const { document, selection } = editor.value;
    const isTextRange = TextRange.isTextRange(selection);
    const titleFragment = expandable.getTitleFragment(node);
    const bodyFragment = expandable.getBodyFragment(node);
    const hasBlockSelectionInBody =
        isBlockRangeSelection(selection) &&
        selection.anchorBlock &&
        bodyFragment.hasDescendant(selection.anchorBlock);
    const hasCursorInBody = isTextRange && selection.hasEdgeIn(bodyFragment);
    const frameIsFocused = isFocused && isTextRange && !selection.hasFocusInFragmentOf(node);

    const [expandables, setExpandables] = useRecoilState(expandablesAtom);

    const { anchorElement, blockProps } = useBlockAnchor(node, {
        label: 'Direct link to expandable block',
    });

    const isExpanded = Boolean(
        hasBlockSelectionInBody ||
            hasCursorInBody ||
            expandables[node.key] === 'expanded' ||
            (expandables[node.key] === undefined && hasHashInBody)
    );

    const onPressToggle = useEventCallback(() => {
        // Focus the right fragment depending on the current state
        editor.change((change) => {
            if (isExpanded) {
                change.collapseToEndOf(titleFragment);
            } else {
                change.collapseToStartOf(bodyFragment);
            }

            change.focus();
        });
        blockProps.toggleAnchorHash(isExpanded);
        setExpandables((state) => ({
            ...state,
            [node.key]: isExpanded ? 'collapsed' : 'expanded',
        }));
    });

    // Expand the block if the cursor is in the body
    // to keep it expanded when he lost focus
    React.useEffect(() => {
        if (hasCursorInBody) {
            setExpandables((state) => ({ ...state, [node.key]: 'expanded' }));
        }
    }, [hasCursorInBody, setExpandables, node.key]);

    const bodyId = React.useId();
    const titleId = React.useId();

    // Check if the previous/next non-diff blocks are expandables to apply the right styles
    const prevBlock = document.getPreviousBlock(node.key);
    const prevTrueBlock =
        prevBlock?.type === 'diff' ? document.getPreviousBlock(prevBlock.key) : prevBlock;
    const isPrevBlockExpandable = prevTrueBlock?.type === 'expandable';

    const nextBlock = document.getNextBlock(node.key);
    const nextTrueBlock =
        nextBlock?.type === 'diff' ? document.getNextBlock(nextBlock.key) : nextBlock;
    const isNextBlockExpandable = nextTrueBlock?.type === 'expandable';

    /** In read-only we reproduce the behavior of GBO, the expandable is a `details` element
     * and its whole `summary` can be pressed to toggle it. */
    if (editor.readOnly) {
        return (
            <BlockWrapper {...props}>
                <VoidBlockFrame {...props} isFocused={frameIsFocused}>
                    <BlockExpandableContent
                        blockId={blockProps.id}
                        readOnly={editor.readOnly}
                        isExpanded={isExpanded}
                        isPrevBlockExpandable={isPrevBlockExpandable}
                        isNextBlockExpandable={isNextBlockExpandable}
                    >
                        <Pressable
                            onPress={onPressToggle}
                            aria-expanded={isExpanded}
                            aria-controls={bodyId}
                            aria-labelledby={titleId}
                        >
                            {/* biome-ignore lint/a11y/noStaticElementInteractions: `summary` is static
                            but we need to prevent its native toggle behavior, hence the `onClick`. */}
                            <summary
                                onClick={(e) => e.preventDefault()}
                                className={clsx(
                                    'flex cursor-pointer items-start gap-2',
                                    blockProps.className
                                )}
                            >
                                <Icon.ChevronRight className="mt-5 ml-5 size-4 shrink-0 transition-transform group-open/expandable:rotate-90" />

                                <BlockExpandableTitle titleId={titleId}>
                                    {anchorElement}
                                </BlockExpandableTitle>
                            </summary>
                        </Pressable>

                        <BlockExpandableBody
                            titleId={titleId}
                            bodyId={bodyId}
                            isExpanded={isExpanded}
                        />
                    </BlockExpandableContent>
                </VoidBlockFrame>
            </BlockWrapper>
        );
    }

    /** In edit mode, we need the expandable elements to be editable,
     * so we use divs (which don't have native toggle behavior like `summary` does)
     * and the chevron becomes a button and handles the toggle behavior. */
    return (
        <BlockWrapper {...props}>
            <VoidBlockFrame {...props} isFocused={frameIsFocused}>
                <div className="relative">
                    <Button
                        kind="blank"
                        label={isExpanded ? 'Collapse' : 'Expand'}
                        leadingIcon={isExpanded ? Icon.ChevronDown : Icon.ChevronRight}
                        iconOnly
                        onPress={onPressToggle}
                        aria-expanded={isExpanded}
                        aria-controls={bodyId}
                        aria-labelledby={titleId}
                        className="absolute top-3 left-3"
                    />

                    <BlockExpandableContent
                        blockId={blockProps.id}
                        readOnly={editor.readOnly}
                        isExpanded={isExpanded}
                        isPrevBlockExpandable={isPrevBlockExpandable}
                        isNextBlockExpandable={isNextBlockExpandable}
                    >
                        <div className={clsx('ml-11 flex items-start gap-2', blockProps.className)}>
                            <BlockExpandableTitle titleId={titleId}>
                                {anchorElement}
                            </BlockExpandableTitle>
                        </div>

                        <BlockExpandableBody
                            titleId={titleId}
                            bodyId={bodyId}
                            isExpanded={isExpanded}
                        />
                    </BlockExpandableContent>
                </div>
            </VoidBlockFrame>
        </BlockWrapper>
    );
}

function BlockExpandableContent(props: {
    blockId: string;
    isExpanded: boolean;
    readOnly: boolean;
    isPrevBlockExpandable: boolean;
    isNextBlockExpandable: boolean;
    children?: React.ReactNode;
}) {
    const {
        blockId,
        isExpanded,
        readOnly,
        isPrevBlockExpandable,
        isNextBlockExpandable,
        children,
    } = props;
    const Tag = readOnly ? 'details' : 'div';

    return (
        <Tag
            id={blockId}
            open={isExpanded}
            className={clsx(
                'group/expandable h-full break-inside-avoid rounded-md border bg-base focus-within:border-primary hover:border-primary',
                isPrevBlockExpandable && 'rounded-t-none',
                isNextBlockExpandable &&
                    'rounded-b-none border-b-transparent hover:border-b-primary'
            )}
        >
            {children}
        </Tag>
    );
}

function BlockExpandableTitle(props: { titleId: string; children?: React.ReactNode }) {
    const { titleId, children } = props;
    return (
        <>
            <div id={titleId} className="flex-1">
                <BlockVariantContext.Provider value="expandableTitle">
                    <FragmentRenderer fragment={FRAGMENT_EXPANDABLE_TITLE} />
                </BlockVariantContext.Provider>
            </div>
            <div className="mt-5 mr-5 flex shrink-0 text-ui-heading-small">{children}</div>
        </>
    );
}

function BlockExpandableBody(props: { titleId: string; bodyId: string; isExpanded: boolean }) {
    const { bodyId, titleId, isExpanded } = props;
    return (
        <section
            id={bodyId}
            aria-labelledby={titleId}
            className={clsx(
                'ml-7',
                isExpanded ? 'h-full p-4 pt-0' : 'invisible h-0 overflow-hidden p-0'
            )}
        >
            <FragmentRenderer fragment={FRAGMENT_EXPANDABLE_BODY} />
        </section>
    );
}

function checkHasHashInBody(sectionIDs: SectionIDs, node: Block, selectedSection: string | null) {
    for (const [key, id] of sectionIDs.entries()) {
        if (id === selectedSection) {
            const selectedNode = node.getNode(key);
            return selectedNode ?? null;
        }
    }
    return null;
}

export function useHasHashInBody(node: Block) {
    const { sections: sectionIDs, selected: selectedSection } =
        React.useContext(EditorSectionIDsContext);

    const [hasHashInBody] = React.useState(() =>
        checkHasHashInBody(sectionIDs, node, selectedSection)
    );
    return hasHashInBody;
}
